/**
 * Shared altitude math for the 3D aircraft layer. Mirrors the MapLibre style
 * expressions in `aircraftSource.ts` (`ALTITUDE_HEIGHT_EXPR`,
 * `ALTITUDE_CAP_SLICE_EXPR`) so a custom Three.js layer can place a model at
 * the exact top of the matching fill-extrusion pillar.
 */

import type maplibregl from 'maplibre-gl';
import { ALTITUDE_EXAGGERATION } from './aircraftSource';

const FEET_TO_METERS = 0.3048;
const KTS_TO_MPS = 0.514444;
const FPM_TO_MPS = FEET_TO_METERS / 60;

/** Damping factor applied to `altM` at a given zoom (matches ALTITUDE_HEIGHT_EXPR). */
function altitudeDampFactor(zoom: number): number {
  if (zoom <= 9) return 1;
  if (zoom >= 14) return 0.15;
  // Piecewise-linear stops: 9->1, 11->0.6, 13->0.3, 14->0.15
  const stops: [number, number][] = [
    [9, 1],
    [11, 0.6],
    [13, 0.3],
    [14, 0.15],
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const [z0, f0] = stops[i];
    const [z1, f1] = stops[i + 1];
    if (zoom >= z0 && zoom <= z1) {
      const t = (zoom - z0) / (z1 - z0);
      return f0 + (f1 - f0) * t;
    }
  }
  return 0.15;
}

/** Exaggerated altitude in meters (same as the GeoJSON `altM` property). */
export function altitudeMeters(altFeet: number): number {
  return altFeet * FEET_TO_METERS * ALTITUDE_EXAGGERATION;
}

/**
 * Pillar top height (meters above ground) at the current zoom. Equals the
 * fill-extrusion `height` value, so a model placed here sits flush on the cap.
 */
export function altitudeHeightAtZoom(altFeet: number, zoom: number): number {
  return altitudeMeters(altFeet) * altitudeDampFactor(zoom);
}

/**
 * Scene Z (metres up) for an airborne aircraft in the custom Three.js layer.
 * Uses terrain elevation (with MapLibre exaggeration) plus exaggerated baro altitude.
 */
export function aircraftSceneAltitudeM(
  map: maplibregl.Map,
  lon: number,
  lat: number,
  altFeet: number,
  zoom: number,
  anchorElevM: number,
): number {
  const terrainM = map.queryTerrainElevation([lon, lat]) ?? 0;
  return terrainM - anchorElevM + altitudeHeightAtZoom(altFeet, zoom);
}

/**
 * Thin slab height at the pillar base (matches ALTITUDE_CAP_SLICE_EXPR). The
 * colored pillar starts on top of this black shadow slab, so a model that
 * should sit on the colored cap is placed at `capSlice + (height - capSlice)`.
 */
export function altitudeCapSliceAtZoom(zoom: number): number {
  const stops: [number, number][] = [
    [0, 300],
    [6, 300],
    [9, 150],
    [11, 75],
    [13, 50],
    [14, 40],
  ];
  if (zoom <= stops[0][0]) return stops[0][1];
  for (let i = 0; i < stops.length - 1; i++) {
    const [z0, v0] = stops[i];
    const [z1, v1] = stops[i + 1];
    if (zoom >= z0 && zoom <= z1) {
      const t = z1 === z0 ? 0 : (zoom - z0) / (z1 - z0);
      return v0 + (v1 - v0) * t;
    }
  }
  return stops[stops.length - 1][1];
}

/**
 * East/North/Up meter offset from a reference lng/lat to a target lng/lat,
 * using the same equirectangular projection as `aircraftShapeRing`.
 */
export function lngLatToEnu(
  refLon: number,
  refLat: number,
  lon: number,
  lat: number,
): { east: number; north: number } {
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos((refLat * Math.PI) / 180);
  return {
    east: (lon - refLon) * mPerDegLon,
    north: (lat - refLat) * mPerDegLat,
  };
}

/**
 * Yaw (degrees, clockwise from north) for a Three.js model whose nose points
 * along +Y in its local frame. The silhouette math in `aircraftShapeRing`
 * rotates (x=wing, y=nose) by `track` so nose-heading == track; the same
 * convention is applied here so the 3D model aligns with the hologram and
 * ground icon.
 */
export function trackToYawDeg(track: number | null): number {
  return track ?? 0;
}

/**
 * Flight-path pitch (degrees, nose-up positive) inferred from vertical rate
 * and ground speed. Returns 0 for level / slow / missing data so cruise
 * flight stays flat. Clamped to +/- 25 deg so it reads as a gentle attitude
 * cue rather than a stall.
 */
export function inferFlightPathPitch(
  verticalRateFpm: number | null,
  groundSpeedKt: number | null,
): number {
  if (verticalRateFpm == null || groundSpeedKt == null) return 0;
  if (Math.abs(verticalRateFpm) < 80) return 0;
  if (groundSpeedKt < 40) return 0;
  const vUp = verticalRateFpm * FPM_TO_MPS;
  const vHoriz = groundSpeedKt * KTS_TO_MPS;
  const pitchRad = Math.atan2(vUp, vHoriz);
  const PITCH_LIMIT_DEG = 25;
  return Math.max(-PITCH_LIMIT_DEG, Math.min(PITCH_LIMIT_DEG, (pitchRad * 180) / Math.PI));
}
